'use client';

import React from 'react';
import {pushToDataLayer, initializeDataLayer} from '@udemy-v2/gtag';
import {useCoursePrice} from '@udemy-v2/react-course-pricing';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';

/**
 * Determines whether dataLayer should be updated based on whether data has changed
 * Compares the actual data payload to detect any meaningful changes
 * @param lastDataJson - JSON string of last pushed data
 * @param currentDataJson - JSON string of current data
 * @returns true if data should be pushed, false if it's a duplicate
 */
function shouldPushToDataLayer(lastDataJson: string, currentDataJson: string): boolean {
    return lastDataJson !== currentDataJson;
}

/**
 * Course Landing Page GTM DataLayer Component
 *
 * This component manages the GTM dataLayer for course landing pages,
 * pushing course and pricing data when rendered within a ClpCourseProvider context.
 *
 * Race Condition Prevention:
 * - Ensures window.dataLayer is initialized before pushing data
 * - GTM's dataLayer works as a queue - data pushed before GTM loads is preserved
 * - When GTM loads, it processes all queued dataLayer items
 *
 * Duplicate Prevention:
 * - Tracks pushed data using refs to prevent duplicate pushes
 * - Only pushes when course ID or price status changes
 * - Prevents React re-renders from causing multiple identical dataLayer pushes
 *
 * DOM Integration:
 * - Adds data-clp-course-id attribute to <body> element for GTM variable access
 * - Automatically removes attribute on unmount
 *
 * It must be rendered INSIDE the ClpCourseProvider to have access to course data.
 */
export function ClpGtmDataLayer() {
    const course = useClpCourse();
    const coursePrice = useCoursePrice(Number(course.id));

    // Track the last pushed data as a JSON string to detect any changes
    const lastDataJsonRef = React.useRef<string>('');

    // Push course data to dataLayer when it becomes available
    React.useEffect(() => {
        // CRITICAL: Initialize dataLayer first to prevent race condition
        // This ensures the array exists before we push to it, regardless of GTM load timing
        initializeDataLayer();

        // Build course data matching production dataLayer structure
        const courseData: Record<string, unknown> = {
            // Course identification
            course_id: course.id,
            course_title: course.title,
            course_url: course.urlCourseLanding,

            // Course categorization
            course_topic: course.primaryTopic?.name || course.topics?.[0]?.name,
            course_category: course.categories?.[0]?.name,
            course_subcategory: course.categories?.[0]?.subcategories?.[0]?.name,

            // Course metadata
            course_language: course.locale?.split('_')[0] || 'en',
            course_locale: course.locale || 'en_US',
            course_length_minutes: course.durationInSeconds
                ? Math.round(course.durationInSeconds / 60)
                : undefined,
            course_num_enrollments: course.enrollments?.count,

            // Instructor info
            course_instructor_name: course.instructors?.[0]?.name,
            instructor_id: course.instructors?.[0]?.id,
            instructor_name: course.instructors?.[0]?.name, // Legacy field

            // Ratings
            course_avg_rating: course.rating?.average,
            rating: course.rating?.average, // Legacy field
            num_reviews: course.rating?.count,

            // Enrollment status
            is_paid: !course.isFree,
            is_free: course.isFree,
            is_enrolled: course.isEnrolled,

            // Tracking
            frontend_tracking_id: course.frontendTrackingId,
            is_direct_landed: course.isDirectLanded,

            // Ecommerce items array for GA4
            items: [
                {
                    item_id: String(course.id),
                    item_name: course.title,
                    item_category: course.categories?.[0]?.name,
                    item_category2: course.categories?.[0]?.subcategories?.[0]?.name,
                    item_category3: course.primaryTopic?.name || course.topics?.[0]?.name,
                },
            ],
        };

        // Add pricing data if available and loaded
        if (coursePrice?.status === 'loaded') {
            courseData.list_price = coursePrice.listPrice?.amount;
            courseData.list_price_currency = coursePrice.listPrice?.currency;
            courseData.price = coursePrice.price?.amount;
            courseData.price_currency = coursePrice.price?.currency;
            courseData.has_discount = !!coursePrice.discountPrice;

            // Calculate discount percentage if both prices are available
            if (coursePrice.listPrice?.amount && coursePrice.discountPrice?.amount) {
                const discountPercent = Math.round(
                    ((coursePrice.listPrice.amount - coursePrice.discountPrice.amount) /
                        coursePrice.listPrice.amount) *
                        100,
                );
                courseData.discount_percent = discountPercent;
            }
        }

        // Compare with last pushed data to prevent duplicates
        const currentDataJson = JSON.stringify(courseData);
        if (!shouldPushToDataLayer(lastDataJsonRef.current, currentDataJson)) {
            // Skip duplicate push - data hasn't changed
            return;
        }

        // Update tracking ref with current data
        lastDataJsonRef.current = currentDataJson;

        // Push to dataLayer using the gtag utility
        // This is safe because we initialized dataLayer above
        pushToDataLayer(courseData);

        // Fire course-data-ready event to match production behavior
        // This allows GTM tags to trigger when course data is available
        pushToDataLayer({
            event: 'course-data-ready',
        });
    }, [course, coursePrice]);

    // Add data-clp-course-id attribute to body element for GTM variable access
    React.useEffect(() => {
        document.body.setAttribute('data-clp-course-id', course.id);

        // Cleanup: remove attribute when component unmounts
        return () => {
            document.body.removeAttribute('data-clp-course-id');
        };
    }, [course.id]);

    // This component doesn't render anything
    return null;
}
